// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { RUNTIME, type Runtime } from '../native/platform.js';

// ─────────────────────────────────────────────────────────────────────────────
// Sync-server base URL resolution — per runtime.
//
// The web app is served same-origin behind nginx, which proxies `/api` to the
// sync-server, so a RELATIVE base (`/api`) works there. The packaged native
// shells are different: they load the UI from their own webview origin
// (`tauri://localhost`, `https://localhost` on Android), where a relative
// `/api/...` resolves against that dead local origin instead of any server. So
// the native shells need an ABSOLUTE base pointing straight at the sync-server.
//
// All three runtimes wrap the SAME Vite `dist`. `VITE_API_URL` is baked into that
// build for the web deployment (`/api`, or the Docker image's
// `https://localhost/api`) — so the native runtimes must NOT inherit it, or they
// would pick up the relative `/api` that is dead inside their webview. Native
// therefore keys off a SEPARATE optional build var, `VITE_NATIVE_API_URL`, and
// otherwise falls back to the per-runtime dev default below. A real user points
// the app at their own server via the Server URL field on the login/register
// screens (persisted as `hushold_syncurl`), which overrides all of this at runtime.
// ─────────────────────────────────────────────────────────────────────────────

// Treat an empty string as "unset" so a blank var falls back rather than becoming
// a broken empty base.
const VITE_API_URL = (import.meta.env['VITE_API_URL'] as string | undefined) || undefined;
const VITE_NATIVE_API_URL =
  (import.meta.env['VITE_NATIVE_API_URL'] as string | undefined) || undefined;

// Native defaults hit the sync-server DIRECTLY (no `/api` prefix — that prefix is
// an nginx-only convention on the web deployment).
const NATIVE_DEFAULTS: Record<Exclude<Runtime, 'web'>, string> = {
  // Desktop shares the host machine's loopback.
  tauri: 'http://localhost:3001',
  // The Android emulator reaches the host machine at 10.0.2.2 (localhost inside
  // the emulator is the device itself).
  capacitor: 'http://10.0.2.2:3001',
};

// The default sync-server base URL for the given runtime (defaults to the current
// runtime). Callers may override the result at runtime via the Server URL field.
export function resolveDefaultSyncUrl(runtime: Runtime = RUNTIME): string {
  if (runtime === 'web') return VITE_API_URL ?? '/api';
  return VITE_NATIVE_API_URL ?? NATIVE_DEFAULTS[runtime];
}
